<template>
  <div class="stat-tile" :class="[`stat-tile--${size}`, cell && 'stat-tile--cell', labelBelow && 'stat-tile--label-below']">
    <p v-if="!labelBelow" class="label-caps stat-tile-label">{{ label }}</p>
    <AppSkeleton v-if="loading" height="28px" width="70%" />
    <p v-else class="editorial-number stat-tile-value" :class="toneClass">
      <slot name="value">{{ value }}</slot>
    </p>
    <p v-if="labelBelow" class="label-caps stat-tile-label">{{ label }}</p>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import AppSkeleton from "./AppSkeleton.vue"

/**
 * Small-caps label + big editorial number — the stat readout every view used
 * to compose by hand from .label-caps + .editorial-number. Value color comes
 * from the semantic tone, the font size from `size` ("fluid" grows from xl to
 * 3xl at the tablet breakpoint — use it in the dense top-of-view stat grids).
 * `cell` adds the ledger-cell chrome (padding + bottom rule) those grids use;
 * tiles inside cards leave it off. Extra footnotes go in the default slot.
 */
const props = withDefaults(
  defineProps<{
    label: string
    value?: string | number
    tone?: "default" | "success" | "danger" | "warning" | "info" | "accent"
    size?: "sm" | "md" | "lg" | "fluid"
    /** Render an AppSkeleton in place of the value while data loads. */
    loading?: boolean
    /** Ledger-cell chrome: padding + border-bottom, compact on phones. */
    cell?: boolean
    /** Value first, label underneath (compact metric clusters). */
    labelBelow?: boolean
  }>(),
  { size: "fluid", tone: "default" },
)

const toneClass = computed(
  () =>
    ({
      default: undefined,
      success: "text-success",
      danger: "text-danger",
      warning: "text-warning",
      info: "text-info",
      accent: "text-accent",
    })[props.tone],
)
</script>
